import React, { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

export type Theme =
  | 'light'
  | 'dark'
  | 'ocean'
  | 'forest'
  | 'sunset'
  | 'purple'
  | 'emerald'
  | 'rose'
  | 'blue'
  | 'blue-dark';

export interface ThemeOption {
  name: Theme;
  label: string;
  description: string;
  preview: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  themes: ThemeOption[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export { ThemeContext };
const themes = [
  {
    name: 'light' as Theme,
    label: 'Light',
    description: 'Sacred saffron robes of Buddhist and Hindu monks',
    preview: {
      primary: '#E96E00',
      secondary: '#FFFBF7',
      accent: '#E96E00',
    },
  },
  {
    name: 'dark' as Theme,
    label: 'Dark',
    description: 'Dark sacred saffron theme with monk robe elegance',
    preview: {
      primary: '#E96E00',
      secondary: '#1E1E1E',
      accent: '#E96E00',
    },
  },
  {
    name: 'blue' as Theme,
    label: 'Blue',
    description: 'Clean and minimal light theme',
    preview: {
      primary: '#3B82F6',
      secondary: '#F8FAFC',
      accent: '#1E40AF',
    },
  },
  {
    name: 'blue-dark' as Theme,
    label: 'Blue Dark',
    description: 'Modern dark theme for low-light environments',
    preview: {
      primary: '#3B82F6',
      secondary: '#1F2937',
      accent: '#60A5FA',
    },
  },
  {
    name: 'ocean' as Theme,
    label: 'Ocean',
    description: 'Calming blue and teal ocean depths',
    preview: {
      primary: '#0891B2',
      secondary: '#0F172A',
      accent: '#22D3EE',
    },
  },
  {
    name: 'forest' as Theme,
    label: 'Forest',
    description: 'Natural green theme inspired by deep forests',
    preview: {
      primary: '#059669',
      secondary: '#064E3B',
      accent: '#34D399',
    },
  },
  {
    name: 'sunset' as Theme,
    label: 'Sunset',
    description: 'Warm orange and amber sunset vibes',
    preview: {
      primary: '#EA580C',
      secondary: '#7C2D12',
      accent: '#FB923C',
    },
  },
  {
    name: 'purple' as Theme,
    label: 'Purple',
    description: 'Royal purple theme with mystical elegance',
    preview: {
      primary: '#7C3AED',
      secondary: '#2E1065',
      accent: '#A855F7',
    },
  },
  {
    name: 'emerald' as Theme,
    label: 'Emerald',
    description: 'Sophisticated emerald green luxury',
    preview: {
      primary: '#059669',
      secondary: '#022C22',
      accent: '#10B981',
    },
  },
  {
    name: 'rose' as Theme,
    label: 'Rose',
    description: 'Elegant rose and pink romantic theme',
    preview: {
      primary: '#E11D48',
      secondary: '#4C0519',
      accent: '#F43F5E',
    },
  },
];

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first, then system preference
    const saved = localStorage.getItem('theme') as Theme;
    if (saved && themes.find(t => t.name === saved)) {
      return saved;
    }

    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  });

  useEffect(() => {
    // Save theme to localStorage
    localStorage.setItem('theme', theme);

    // Apply theme to document
    const root = document.documentElement;

    // Remove all theme classes
    root.classList.remove(...themes.map(t => t.name));

    // Add current theme class
    root.classList.add(theme);

    // Set CSS custom properties for dynamic theming
    const themeConfig = themes.find(t => t.name === theme);
    if (themeConfig) {
      root.style.setProperty('--theme-primary', themeConfig.preview.primary);
      root.style.setProperty(
        '--theme-secondary',
        themeConfig.preview.secondary
      );
      root.style.setProperty('--theme-accent', themeConfig.preview.accent);
    }
  }, [theme]);

  const value = {
    theme,
    setTheme,
    themes,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
